"use client";

import { useMemo, useState } from "react";
import {
    Avancer,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    pourcent,
    remplir,
    useParcours,
} from "./Journey";
import {
    trimestresRetraite,
    partRevenuCotise,
    PART_RETRAITE_BASE,
    TAUX_RETRAITE_BASE,
    REVENU_PAR_TRIMESTRE,
    SMIC_HORAIRE_REFERENCE,
    HEURES_PAR_TRIMESTRE,
    PALIERS_CIPAV,
} from "@/lib/tools/microEnterprise";
import { expliquerTrimestres } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";

/**
 * Combien de trimestres de retraite mon chiffre d'affaires valide-t-il ?
 *
 * LE FAIT QUE PERSONNE N'ÉCRIT : à chiffre d'affaires égal, un vendeur et un
 * libéral ne valident pas le même nombre de trimestres. La retraite se calcule
  * sur le revenu cotisé, reconstitué à partir de la part des cotisations qui va
  * à la retraite de base (Service-Public, F23369) : 29,9 % du chiffre d'affaires
  * en vente, 66,5 % en libéral. Il faut donc encaisser 24 115 € en vente pour
 * valider quatre trimestres, contre 10 850 € en libéral : plus de deux fois
 * plus, pour exactement les mêmes droits.
 *
 * TROIS QUESTIONS, PUIS LE RÉSULTAT. La situation d'abord, parce qu'elle
 * oriente : le plafond de quatre trimestres par an est commun à tous les
 * régimes, un salarié n'a donc rien à gagner en trimestres, et un retraité
 * n'ouvre aucun droit nouveau (on ne lui demande alors ni activité ni
 * chiffre). L'activité ensuite, parce que c'est elle qui décide de la
 * mécanique (barème Cipav en chiffre d'affaires, ou règle des 150 heures de
  * SMIC sur le revenu cotisé), et le chiffre d'affaires en dernier,
 * la seule donnée qu'un micro-entrepreneur ait sous la main. Le curseur reste
 * manipulable sur le résultat : c'est en le déplaçant qu'on voit un trimestre
 * tomber d'un coup, et qu'on comprend que le calcul tronque.
 *
 * ZÉRO TRIMESTRE N'EST PAS UN VERDICT SEC : le résultat nomme le seuil qui
 * n'est pas atteint et ce qu'il manque pour l'atteindre. Et la comparaison
 * entre activités se fait au chiffre d'affaires saisi, pas sur un exemple :
 * le lecteur voit ce que SON chiffre vaudrait dans une autre activité.
 */

const PHASES = { situation: 0, activite: 0, ca: 1, resultat: 2 };

const SITUATIONS = ["seul", "salarie", "retraite"];

const ICONES_SITUATION = {
    seul: "user-rounded-linear",
    salarie: "buildings-linear",
    retraite: "history-linear",
};

/** Icônes des quatre activités, les mêmes que sur le simulateur de revenu net. */
const ICONES = {
    vente: "sale-linear",
    services: "pen-2-linear",
    bnc: "brain-linear",
    cipav: "medal-ribbon-star-linear",
};

const ACTIVITES = ["vente", "services", "bnc", "cipav"];

/**
 * Le curseur s'arrête bien au-delà de ce qu'exige chaque activité pour quatre
  * trimestres : 24 115 € en vente, moins de 15 000 € partout ailleurs.
 */
const CA_MAXIMUM = { vente: 60000, services: 40000, bnc: 40000, cipav: 40000 };
const CA_PAS = 250;
const CA_DEPART = 12000;

/**
 * La part du chiffre d'affaires qui devient du revenu cotisé, lue dans la lib
 * et non recopiée : si les taux changent, les textes suivent. Null pour la
 * Cipav, dont les trimestres suivent un barème en chiffre d'affaires (les
 * textes n'en affichent donc aucune pour elle).
 */
const PART = {
    vente: partRevenuCotise("vente"),
    services: partRevenuCotise("services"),
    bnc: partRevenuCotise("bnc"),
    cipav: null,
};

/** Une part à une décimale : « 29,9 % » en français, "29.9%" en anglais. */
function part(cle, lang) {
    return PART[cle] === null ? "" : pourcent(PART[cle], lang, 1);
}

const L = {
    fr: {
        phases: ["Votre situation", "Votre chiffre d'affaires", "Vos trimestres"],
        retour: "Retour",
        continuer: "Voir mes trimestres",
        recommencer: "Refaire le calcul",

        qSituation: "Quelle est votre situation, à côté de la micro-entreprise ?",
        qSituationAide:
            "Le plafond de quatre trimestres par an vaut pour tous vos régimes réunis : un salaire qui en valide déjà quatre ne laisse rien à ajouter à la micro, et une retraite que vous touchez déjà ne s'augmente plus.",
        seul: "La micro est mon seul revenu",
        seulDetail: "Aucun salaire ni pension à côté : seule la micro valide vos trimestres",
        salarie: "J'ai un emploi salarié à côté",
        salarieDetail: "À temps plein ou à temps partiel : votre salaire valide déjà des trimestres",
        retraite: "Je suis déjà à la retraite",
        retraiteDetail: "Une pension que vous touchez déjà, la micro vient en complément",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Vos droits se calculent sur la part de vos cotisations qui va à la retraite de base, reconvertie en revenu. Selon l'activité, elle va de {vente} à {bnc} de votre chiffre d'affaires. Pour la même somme encaissée, cela peut faire la différence entre un et quatre trimestres.",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, hôtellerie, restauration. {part} du chiffre d'affaires compte pour la retraite",
        services: "Prestations de services",
        servicesDetail:
            "Métiers manuels et commerciaux : bâtiment, coiffure, transport, chambres d'hôtes. {part} du chiffre d'affaires compte pour la retraite",
        bnc: "Activité libérale",
        bncDetail:
            "Métiers intellectuels et freelances : développement, design, conseil, formation. {part} du chiffre d'affaires compte pour la retraite",
        cipav: "Profession libérale réglementée",
        cipavDetail:
            "Votre caisse de retraite est la Cipav : architecte, psychologue, ostéopathe. Ses trimestres suivent des seuils à part",

        qCa: "Combien encaissez-vous dans l'année ?",
        qCaAide:
            "Ce que vous encaissez réellement en 2026, pas ce que vous facturez : vos cotisations se paient sur l'encaissé, et ce sont elles qui valident vos trimestres. Une estimation suffit, vous pourrez l'ajuster sur le résultat.",
        ca: "Chiffre d'affaires encaissé dans l'année",
        caAide: "Déplacez le curseur : tout se recalcule.",

        resultatTitre: "Vos trimestres",
        valides: "Trimestres validés",
        surQuatre: "sur 4",
        revenuRetenu: "Revenu cotisé pour la retraite",
        revenuRetenuAide:
            "La part de vos cotisations qui va à la retraite de base, divisée par le taux de cette cotisation. Vos trimestres se comptent sur ce montant.",
        caQuatreTitre: "Chiffre d'affaires pour quatre trimestres à la Cipav",
        caQuatreAide:
            "Barème publié par l'Urssaf pour les seuls affiliés à la Cipav, exprimé en chiffre d'affaires et non en revenu.",

        aucunTitre: "À ce niveau, vous ne validez aucun trimestre",
        aucunSmic:
            "Votre revenu cotisé, {revenu}, reste sous les {seuil} qu'exige un trimestre. Il vous manque {manque} de chiffre d'affaires pour atteindre le premier, soit {cible} encaissés dans l'année.",
        aucunCipav:
            "Le barème de la Cipav demande {seuil} de chiffre d'affaires pour un premier trimestre. Il vous manque {manque} pour l'atteindre.",
        anneeCivile:
            "Le compteur se referme au 31 décembre : seul ce que vous encaissez dans l'année compte, pas la date à laquelle vous avez commencé, et un encaissement de janvier ira à l'année suivante.",
        prochain: "Il vous manque {manque} pour valider un trimestre de plus",
        prochainAide:
            "Soit {cible} de chiffre d'affaires encaissé dans l'année. En dessous, même d'un euro, le trimestre n'est pas validé.",
        prochainQuatre: "Et {quatre} pour les quatre trimestres de l'année.",
        complet:
            "Vous validez le maximum : on ne peut pas valider plus de quatre trimestres par an, quel que soit le chiffre d'affaires. Ce que vous encaissez au-delà de {cible} ne validera aucun trimestre de plus, mais augmente le revenu porté à votre compte, celui sur lequel votre pension sera calculée.",

        salarieTitre: "Votre salaire valide déjà des trimestres",
        salarieTexte:
            "Le compte se fait tous régimes réunis, quatre trimestres au plus par an, et un emploi salarié en valide souvent les quatre à lui seul. S'il en valide moins de quatre, la micro peut compléter jusqu'à quatre. S'il les valide déjà, ce chiffre d'affaires n'en ajoute aucun, quel que soit son montant, mais il augmente le revenu porté à votre compte, celui sur lequel votre pension sera calculée. Le chiffre ci-dessus est ce que la micro validerait à elle seule.",
        retraiteTitre: "Ces cotisations n'ouvrent aucun droit nouveau",
        retraiteTexte:
            "Une fois que vous touchez votre retraite, cotiser en micro-entreprise n'augmente ni la retraite de base ni la retraite complémentaire : il n'y a donc pas de trimestre à compter ici. Si vous ne la touchez pas encore, refaites le calcul avec une autre situation : vos trimestres se comptent alors comme pour tout le monde. En cas de doute, seule votre caisse de retraite peut confirmer votre cas.",

        regleTitre: "D'où vient ce calcul",
        regleSmic:
            "Un trimestre se valide par tranche de revenu cotisé égale à {heures} heures de SMIC horaire au 1er janvier de l'année, soit {heures} × {smic} = {revenu} pour 2026. Ce revenu se reconstitue à partir de vos cotisations : la part qui va à la retraite de base, {partBic} de votre taux global en commerce et en prestations, {partBnc} en libéral, divisée par {taux}, le taux de cette cotisation. C'est la méthode de la fiche officielle de Service-Public. Le SMIC passé à 12,31 € au 1er juin ne change rien aux trimestres de 2026.",
        regleCipav:
            "La Cipav est la seule caisse pour laquelle l'Urssaf publie encore un tableau en chiffre d'affaires : {un} pour un trimestre, {deux} pour deux, {trois} pour trois, {quatre} pour quatre. Ces montants ne valent que pour elle et ne se transposent à aucune autre activité.",

        comparaisonTitre: "Et dans une autre activité ?",
        comparaisonAide:
            "Avec {ca} encaissés, voici ce que chaque activité valide, et ce qu'il faut encaisser pour quatre trimestres. L'écart vient de la part du chiffre d'affaires qui compte pour la retraite : {vente} en vente, {bnc} en libéral. La Cipav, elle, a son barème publié directement en chiffre d'affaires : aucune conversion à faire.",
        vous: "vous",
        pourQuatre: "{montant} pour quatre trimestres",
        pourQuatreCipav: "{montant} pour quatre trimestres à la Cipav",

        avertissementTroncature:
            "Le nombre de trimestres n'est jamais arrondi à la hausse : avec {seuilMoinsUn} de revenu cotisé, vous n'en validez aucun.",
        avertissements: [
            "Seules les cotisations effectivement payées comptent : une déclaration faite mais non réglée ne valide rien.",
            "L'ACRE, la réduction de cotisations des premiers trimestres d'activité, est sans effet ici : vos droits sont calculés sur les cotisations qui auraient été dues sans elle, à condition d'être à jour de vos paiements.",
            "Hors Cipav, l'administration ne publie aucun tableau de chiffre d'affaires par activité : les montants affichés ici suivent sa méthode, et c'est votre relevé de carrière qui fait foi.",
        ],
    },
    en: {
        phases: ["Your situation", "Your turnover", "Your quarters"],
        retour: "Back",
        continuer: "Show my quarters",
        recommencer: "Start over",

        qSituation: "What is your situation alongside the micro-entreprise?",
        qSituationAide:
            "The cap of four quarters a year covers all your schemes together: a salary that already validates four leaves nothing for the micro to add, and a pension you already receive cannot be increased.",
        seul: "The micro is my only income",
        seulDetail: "No salary or pension alongside it: only the micro validates your quarters",
        salarie: "I have a salaried job alongside it",
        salarieDetail: "Full-time or part-time: your salary already validates quarters",
        retraite: "I am already retired",
        retraiteDetail: "A pension you already receive, the micro is a top-up",

        qActivite: "What is your activity?",
        qActiviteAide:
            "Your rights are based on the share of your contributions that goes to the basic pension, converted back into income. Depending on the activity, it ranges from {vente} to {bnc} of your turnover. For the same amount collected, that can make the difference between one quarter and four.",
        vente: "Sale of goods",
        venteDetail: "Retail, hotels, catering. {part} of turnover counts towards your pension",
        services: "Services",
        servicesDetail:
            "Manual and commercial trades: building, hairdressing, transport, bed and breakfast. {part} of turnover counts towards your pension",
        bnc: "Liberal profession",
        bncDetail:
            "Intellectual trades and freelancers: development, design, consulting, training. {part} of turnover counts towards your pension",
        cipav: "Regulated liberal profession",
        cipavDetail:
            "Your pension fund is the Cipav: architect, psychologist, osteopath. Its quarters follow separate thresholds",

        qCa: "How much do you collect over the year?",
        qCaAide:
            "What you actually collect in 2026, not what you invoice: your contributions are paid on cash received, and they are what validates your quarters. An estimate is enough, you can adjust it on the result.",
        ca: "Turnover collected during the year",
        caAide: "Move the slider: everything recalculates.",

        resultatTitre: "Your quarters",
        valides: "Quarters validated",
        surQuatre: "out of 4",
        revenuRetenu: "Income credited for your pension",
        revenuRetenuAide:
            "The share of your contributions that goes to the basic pension, divided by the rate of that contribution. Your quarters are counted on this figure.",
        caQuatreTitre: "Turnover for four quarters under the Cipav scale",
        caQuatreAide:
            "Table published by URSSAF for Cipav members only, expressed in turnover rather than income.",

        aucunTitre: "At this level, you do not validate any quarter",
        aucunSmic:
            "Your credited income, {revenu}, is below the {seuil} a quarter requires. You need {manque} more in turnover to reach the first one, which means {cible} collected over the year.",
        aucunCipav:
            "The Cipav scale requires {seuil} of turnover for a first quarter. You need {manque} more to reach it.",
        anneeCivile:
            "The count closes on 31 December: only what you collect within the year counts, not the date you started, and a January payment goes to the following year.",
        prochain: "You need {manque} more for one more quarter",
        prochainAide:
            "That is {cible} of turnover collected over the year. Even one euro below, the quarter is not validated.",
        prochainQuatre: "And {quatre} for all four quarters of the year.",
        complet:
            "You validate the maximum: no more than four quarters can be validated in a year, whatever the turnover. What you collect beyond {cible} validates no further quarter, but raises the income credited to your account, on which your pension will be based.",

        salarieTitre: "Your salary already validates quarters",
        salarieTexte:
            "Quarters are counted across all your schemes, four at most per year, and a salaried job often validates all four on its own. If it validates fewer than four, the micro can top up to four. If it already validates all four, this turnover adds none, whatever its amount, but it raises the income credited to your account, on which your pension will be based. The figure above is what the micro would validate by itself.",
        retraiteTitre: "These contributions open no new rights",
        retraiteTexte:
            "Once you receive your pension, contributing through a micro-entreprise increases neither the basic pension nor the supplementary pension: there are no quarters to count here. If you do not receive it yet, start over with another situation: your quarters then count like everyone else's. When in doubt, only your pension fund can confirm your position.",

        regleTitre: "Where this calculation comes from",
        regleSmic:
            "One quarter is validated for each band of credited income equal to {heures} hours of the SMIC, the French hourly minimum wage, as it stands on 1 January: {heures} × {smic} = {revenu} for 2026. That income is rebuilt from your contributions: the share that goes to the basic pension, {partBic} of your overall rate in trade and services, {partBnc} in liberal professions, divided by {taux}, the rate of that contribution. This is the method of the official Service-Public page. The SMIC rise to 12.31 € on 1 June changes nothing for this year's quarters.",
        regleCipav:
            "The Cipav is the only fund for which URSSAF still publishes a table in turnover: {un} for one quarter, {deux} for two, {trois} for three, {quatre} for four. These amounts apply to the Cipav alone and cannot be carried over to any other activity.",

        comparaisonTitre: "And in another activity?",
        comparaisonAide:
            "With {ca} collected, here is what each activity validates, and what you need to collect for four quarters. The gap comes from the share of turnover that counts towards your pension: {vente} in sales, {bnc} in liberal professions. The Cipav, for its part, has its scale published directly in turnover: no conversion needed.",
        vous: "you",
        pourQuatre: "{montant} for four quarters",
        pourQuatreCipav: "{montant} for four quarters under the Cipav scale",

        avertissementTroncature:
            "The number of quarters is never rounded up: with {seuilMoinsUn} of credited income, you validate none.",
        avertissements: [
            "Only contributions actually paid count: a return filed but not paid validates nothing.",
            "ACRE, the reduction in contributions during your first quarters of activity, has no effect here: your rights are based on the contributions that would have been due without it, provided your payments are up to date.",
            "Outside the Cipav, the authorities publish no turnover table by activity: the amounts shown here follow their method, and your career statement (relevé de carrière) is what counts.",
        ],
    },
};

/** Les quatre trimestres, en pastilles qui se remplissent. */
function Pastilles({ valides, compact = false }) {
    return (
        <div className={compact ? "flex gap-1 w-20" : "flex gap-2 mt-3"}>
            {[1, 2, 3, 4].map((n) => (
                <div
                    key={n}
                    className={`${compact ? "h-1.5" : "h-2.5"} flex-1 rounded-full transition-colors duration-300 ${
                        n <= valides ? "bg-primary" : "bg-gray-200 dark:bg-white/10"
                    }`}
                />
            ))}
        </div>
    );
}

/** Une activité dans la comparaison : ses trimestres au chiffre saisi, et le chiffre pour quatre. */
function LigneComparaison({ libelle, courante, vous, valides, surQuatre, pourQuatre }) {
    return (
        <div
            className={`flex justify-between items-center gap-4 py-2.5 border-b border-gray-200/70 dark:border-white/10 last:border-0 ${
                courante ? "" : "text-gray-600 dark:text-lightBlue"
            }`}
        >
            <div className="min-w-0">
                <div className={`text-sm leading-snug ${courante ? "font-semibold dark:text-white" : ""}`}>
                    {libelle}
                    {courante && (
                        <span className="ml-2 text-[11px] font-semibold text-[#D04515] dark:text-[#FF8A5B] uppercase tracking-wider">
                            {vous}
                        </span>
                    )}
                </div>
                <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">{pourQuatre}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
                <Pastilles valides={valides} compact />
                <span
                    className={`text-sm tabular-nums whitespace-nowrap min-w-14 text-right ${
                        courante ? "font-bold dark:text-white" : ""
                    }`}
                >
                    {valides} {surQuatre}
                </span>
            </div>
        </div>
    );
}

export default function SimulateurTrimestresRetraite({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    // Le dossier du lecteur, s'il en a un : l'activité et le chiffre d'affaires
    // partent déjà posés. La première question (salarié, retraité…) n'est pas
    // dans le dossier : le parcours part toujours de son début.
    const { valeurs: dossier } = useDossierOutil();
    const [situation, setSituation] = useState(null);
    const [activite, setActivite] = useState(dossier.activite ?? null);
    // Ramené dans la course du curseur de l'activité, comme le fait son choix.
    const [ca, setCa] = useState(
        dossier.ca !== undefined
            ? Math.min(dossier.ca, CA_MAXIMUM[dossier.activite] ?? Infinity)
            : CA_DEPART,
    );

    // Le chiffre d'affaires survit au « Refaire le calcul » : le lecteur qui
    // a réglé son chiffre et veut voir une autre activité ne le repose pas.
    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "situation",
        () => {
            setSituation(null);
            setActivite(null);
        },
        PHASES,
    );

    useSignalerDossier({ activite, ca }, etape === "resultat");

    const r = useMemo(
        () => (activite ? trimestresRetraite({ chiffreAffaires: ca, activite }) : null),
        [ca, activite],
    );

    // La comparaison se fait au chiffre saisi, pour les quatre activités :
    // calculée et non recopiée, elle suit le SMIC de référence toute seule.
    const comparaison = useMemo(
        () => ACTIVITES.map((a) => ({ cle: a, ...trimestresRetraite({ chiffreAffaires: ca, activite: a }) })),
        [ca],
    );

    // Chaque chiffre du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(
        () => (r ? expliquerTrimestres({ chiffreAffaires: ca, activite }, r, lang) : null),
        [r, ca, activite, lang],
    );
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const maximum = CA_MAXIMUM[activite] || CA_MAXIMUM.bnc;

    if (etape === "situation") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.situation}
                titre={t.qSituation}
                aide={t.qSituationAide}
                choix={SITUATIONS.map((cle) => ({
                    icone: ICONES_SITUATION[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setSituation(cle);
                        // Un retraité n'ouvre aucun droit nouveau, quels que
                        // soient l'activité et le chiffre : on ne les demande pas.
                        aller(cle === "retraite" ? "resultat" : "activite");
                    },
                }))}
            />
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={remplir(t.qActiviteAide, {
                    vente: part("vente", lang),
                    bnc: part("bnc", lang),
                })}
                choix={ACTIVITES.map((cle) => ({
                    icone: ICONES[cle],
                    titre: t[cle],
                    detail: remplir(t[`${cle}Detail`], { part: part(cle, lang) }),
                    onClick: () => {
                        setActivite(cle);
                        // Le curseur n'a pas la même course partout : un chiffre
                        // posé en vente est ramené dans celle de l'activité choisie.
                        setCa((c) => Math.min(c, CA_MAXIMUM[cle]));
                        aller("ca");
                    },
                }))}
            />
        );
    }

    if (etape === "ca") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.ca}
                titre={t.qCa}
                aide={t.qCaAide}
            >
                <Curseur
                    libelle={t.ca}
                    valeur={ca}
                    poser={setCa}
                    maximum={maximum}
                    pas={CA_PAS}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("resultat")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (situation === "retraite") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4 rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                    <p className="text-sm font-semibold dark:text-white mb-1">{t.retraiteTitre}</p>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.retraiteTexte}</p>
                </div>
                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </Resultat>
        );
    }

    const manque = r.caProchainTrimestre ? Math.max(0, r.caProchainTrimestre - ca) : 0;
    const smic = `${SMIC_HORAIRE_REFERENCE.toFixed(2).replace(".", lang === "fr" ? "," : ".")} €`;
    // Pour un salarié, le chiffre ne dit pas ses droits mais ce que la micro
    // ferait seule : la carte reste neutre, jamais en alerte.
    const ton =
        situation === "salarie"
            ? "neutre"
            : r.valides === 0
              ? "alerte"
              : r.valides >= 4
                ? "plein"
                : "cours";

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                <Curseur
                    libelle={t.ca}
                    aide={t.caAide}
                    valeur={ca}
                    poser={setCa}
                    maximum={maximum}
                    pas={CA_PAS}
                    formater={(v) => euros(v, lang)}
                />
            </div>

            <IndiceExplication lang={lang} />

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div
                    className={`rounded-xl p-5 border ${
                        ton === "alerte"
                            ? "border-amber-500/50 bg-amber-500/[0.06]"
                            : ton === "plein"
                              ? "border-green-600/40 bg-green-600/[0.06]"
                              : ton === "cours"
                                ? "border-primary/40 bg-primary/[0.06]"
                                : "border-gray-200/70 dark:border-white/10"
                    }`}
                >
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.valides}
                    </div>
                    <div className="text-3xl font-bold tabular-nums dark:text-white">
                        <MontantExplicable explication={ex?.valides} ouvert={ouverte === "valides"} onBasculer={() => basculer("valides")}>
                            {r.valides}
                        </MontantExplicable>{" "}
                        <span className="text-base font-normal text-gray-500 dark:text-lightBlue/70">
                            {t.surQuatre}
                        </span>
                    </div>
                    <Pastilles valides={r.valides} />
                    {ouverte === "valides" && <PanneauExplication explication={ex?.valides} lang={lang} />}
                </div>

                <div className="rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {r.regle === "cipav" ? t.caQuatreTitre : t.revenuRetenu}
                    </div>
                    <div className="text-2xl font-bold tabular-nums dark:text-white">
                        <MontantExplicable explication={ex?.revenu} ouvert={ouverte === "revenu"} onBasculer={() => basculer("revenu")}>
                            <Montant
                                valeur={r.regle === "cipav" ? r.caQuatreTrimestres : r.revenuRetenu}
                                lang={lang}
                                reduit={reduit}
                            />
                        </MontantExplicable>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1">
                        {r.regle === "cipav" ? t.caQuatreAide : t.revenuRetenuAide}
                    </div>
                    {ouverte === "revenu" && <PanneauExplication explication={ex?.revenu} lang={lang} />}
                </div>
            </div>

            <div className="mt-5">
                {situation === "salarie" ? (
                    <>
                        <p className="text-sm font-semibold dark:text-white mb-1">{t.salarieTitre}</p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.salarieTexte}</p>
                    </>
                ) : r.valides === 0 ? (
                    <>
                        <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
                            {t.aucunTitre}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {r.regle === "cipav"
                                ? t.aucunCipav
                                      .replace("{seuil}", euros(PALIERS_CIPAV[0], lang))
                                      .replace("{manque}", euros(manque, lang))
                                : t.aucunSmic
                                      .replace("{revenu}", euros(r.revenuRetenu, lang))
                                      .replace("{seuil}", euros(REVENU_PAR_TRIMESTRE, lang))
                                      .replace("{manque}", euros(manque, lang))
                                      .replace("{cible}", euros(r.caProchainTrimestre, lang))}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.anneeCivile}</p>
                    </>
                ) : r.valides >= 4 ? (
                    <p className="text-sm text-green-700 dark:text-green-400 mb-0">
                        {t.complet.replace("{cible}", euros(r.caQuatreTrimestres, lang))}
                    </p>
                ) : (
                    <>
                        <p className="text-sm font-semibold dark:text-white mb-1">
                            {t.prochain.replace("{manque}", euros(manque, lang))}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                            {t.prochainAide.replace("{cible}", euros(r.caProchainTrimestre, lang))}
                            {r.valides < 3 &&
                                ` ${t.prochainQuatre.replace("{quatre}", euros(r.caQuatreTrimestres, lang))}`}
                        </p>
                    </>
                )}
            </div>

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white">{t.comparaisonTitre}</div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-3">
                    {remplir(t.comparaisonAide, {
                        ca: euros(ca, lang),
                        vente: part("vente", lang),
                        bnc: part("bnc", lang),
                    })}
                </p>
                <div>
                    {comparaison.map((c) => (
                        <LigneComparaison
                            key={c.cle}
                            libelle={t[c.cle]}
                            courante={c.cle === activite}
                            vous={t.vous}
                            valides={c.valides}
                            surQuatre={t.surQuatre}
                            pourQuatre={(c.cle === "cipav" ? t.pourQuatreCipav : t.pourQuatre).replace(
                                "{montant}",
                                euros(c.caQuatreTrimestres, lang),
                            )}
                        />
                    ))}
                </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white">{t.regleTitre}</div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                    {r.regle === "cipav"
                        ? t.regleCipav
                              .replace("{un}", euros(PALIERS_CIPAV[0], lang))
                              .replace("{deux}", euros(PALIERS_CIPAV[1], lang))
                              .replace("{trois}", euros(PALIERS_CIPAV[2], lang))
                              .replace("{quatre}", euros(PALIERS_CIPAV[3], lang))
                        : t.regleSmic
                              .replaceAll("{heures}", HEURES_PAR_TRIMESTRE)
                              .replace("{smic}", smic)
                              .replace("{revenu}", euros(REVENU_PAR_TRIMESTRE, lang))
                              .replace("{partBic}", pourcent(PART_RETRAITE_BASE.services, lang, 2))
                              .replace("{partBnc}", pourcent(PART_RETRAITE_BASE.bnc, lang, 2))
                              .replace("{taux}", pourcent(TAUX_RETRAITE_BASE, lang, 2))}
                </p>
            </div>

            <ul className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 space-y-1.5 list-disc pl-4">
                {r.regle !== "cipav" && (
                    <li>
                        {t.avertissementTroncature.replace(
                            "{seuilMoinsUn}",
                            euros(REVENU_PAR_TRIMESTRE - 1, lang),
                        )}
                    </li>
                )}
                {t.avertissements.map((a) => (
                    <li key={a}>{a}</li>
                ))}
            </ul>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
